import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { useToast } from '../../components/common';
import { nameOf, sharingState } from '../nucleus/PeopleData';

export { nameOf, sharingState };

// ONE PERSON, ONE ID: every People page opens a person by their user id
// (?open=<userId>), whichever ties you have to them. A person you are friends
// with, who also follows you, is one person with two ties — one inspector.

// One follow, read from the side that is the other person (PeopleData's row,
// with the username kept). follower_ep is what the FOLLOWER shares,
// exposure_profile what the FOLLOWED shares.
function tieOf(f, side, realmName) {
    const u = f?.[side] || {};
    const id = u.id || (side === 'followed' ? f?.followed_id : f?.follower_id);
    if (!f?.id || !id) return null;
    const mine = side === 'followed' ? f.follower_ep : f.exposure_profile;
    const theirs = side === 'followed' ? f.exposure_profile : f.follower_ep;
    return {
        id: String(f.id),
        // their photo: the one on the identity they present to you (null without one)
        person: {
            id: String(id), name: nameOf(u), username: u.username || null, avatar: u.avatar_url || null, joined: u.created_at || null,
            // the groups you are both in, when the listing says (null when it does not)
            groups: Array.isArray(u.groups_in_common)
                ? u.groups_in_common.filter((g) => g?.id).map((g) => ({ id: String(g.id), name: g.name || 'a group' }))
                : null,
        },
        realm: mine?.privacy_realm_name || realmName(mine?.privacy_realm_id) || null,
        epID: mine?.id || (side === 'followed' ? f.follower_ep_id : f.exposure_profile_id) || null,
        mine: mine || null,
        theirs: theirs || null,
        at: f.created_at || null,
        since: f.responded_at || f.created_at || null,
        answered: f.responded_at || null,
        ended: f.revoked_at || f.responded_at || null,
        status: f.status,
        kind: f.relationship_type || null,
    };
}

const byName = (a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase());

// Your ties, folded into one entry per person:
//   { id, name, username, friend, incoming, sent, following, follower }
export async function loadTies() {
    // Friend requests are read a status at a time, so ended friendships (Past) never
    // crowd an open request or a friend out of the page limit.
    const friendRequests = (get) => Promise.all(['pending', 'accepted', 'revoked'].map((status) =>
        get({ relationship_type: 'friend_request', status, limit: 100 }).catch(() => ({ follows: [] }))))
        // each row once, under the status it is in (a cancelled ask can come back with 'revoked')
        .then((pages) => ({ follows: pages.flatMap((r, i) => (r?.follows || []).filter((f) => f.status === ['pending', 'accepted', 'revoked'][i])) }));
    const [realms, inF, outF, outFollow, followers, pub, inFollowReq] = await Promise.all([
        api.getPrivacyRealms().catch(() => []),
        friendRequests((q) => api.getIncomingFollows(q)),
        friendRequests((q) => api.getOutgoingFollows(q)),
        api.getOutgoingFollows({ relationship_type: 'follow', limit: 100 }).catch(() => ({ follows: [] })),
        api.getMyFollowers({ limit: 100 }).catch(() => ({ follows: [] })),
        api.getPublicProfile().catch(() => null),
        // asking to follow you, while Open follows is off
        api.getIncomingFollows({ relationship_type: 'follow', status: 'pending', limit: 100 }).catch(() => ({ follows: [] })),
    ]);
    const realmList = Array.isArray(realms) ? realms : realms?.profiles || realms?.realms || [];
    const realmName = (id) => (id ? realmList.find((r) => String(r.id) === String(id))?.name : null);
    const read = (res, side) => (res?.follows || []).map((f) => tieOf(f, side, realmName)).filter(Boolean);

    const people = new Map();
    const at = (tie) => {
        const p = tie.person;
        if (!people.has(p.id)) people.set(p.id, { id: p.id, name: p.name, username: p.username, avatar: null });
        const e = people.get(p.id);
        // a friend's grant presents the face that counts; any tie's photo will do until one does
        if (p.avatar && (!e.avatar || tie.status === 'accepted')) e.avatar = p.avatar;
        // when they joined ofSelf (their public account's created_at)
        if (p.joined && !e.joined) e.joined = p.joined;
        if (p.groups && !e.groups) e.groups = p.groups;
        return e;
    };
    // a friend is an accepted request, from either side, once — one side's
    // contract ended (expired or revoked) is still a friend: the server lists a
    // friendship as 'revoked' only when BOTH sides ended. A revoked one that was
    // answered is a friendship that ended (the latest, once); revoked
    // unanswered is an ask cancelled, and declined was refused: neither is shown.
    for (const [res, side, pending] of [[outF, 'followed', 'sent'], [inF, 'follower', 'incoming']]) {
        for (const t of read(res, side)) {
            const e = at(t);
            if (t.status === 'accepted') { if (!e.friend) e.friend = t; } else if (t.status === 'pending') e[pending] = t;
            else if (t.status === 'revoked' && t.answered && (!e.past || (t.ended || '') > (e.past.ended || ''))) e.past = t;
        }
    }
    for (const t of read(outFollow, 'followed')) {
        if (t.status === 'accepted') at(t).following = t;
        else if (t.status === 'pending' && t.kind === 'follow') at(t).followSent = t;   // you asked; they have not answered
    }
    for (const t of read(inFollowReq, 'follower')) if (t.status === 'pending' && t.kind === 'follow') at(t).followAsked = t;
    for (const t of read(followers, 'follower')) {
        if (t.kind !== 'friend_request' && (!t.status || t.status === 'accepted')) at(t).follower = t;
    }

    // FOLLOWING IS IMPLICIT IN A FRIEND: a friend carries no follow ties, so no
    // follow list holds them and their panel offers no follow acts
    for (const e of people.values()) {
        if (e.friend) { delete e.following; delete e.follower; delete e.followAsked; delete e.followSent; }
    }
    const all = [...people.values()].sort(byName);
    return {
        people,
        friends: all.filter((e) => e.friend),
        incoming: all.filter((e) => e.incoming && !e.friend),
        followAsks: all.filter((e) => e.followAsked && !e.follower && !e.friend),
        followSent: all.filter((e) => e.followSent && !e.following && !e.friend),
        sent: all.filter((e) => e.sent && !e.friend),
        past: all.filter((e) => e.past && !e.friend && !e.incoming && !e.sent),
        following: all.filter((e) => e.following && !e.friend),
        followers: all.filter((e) => e.follower && !e.friend),
        publicOn: pub ? !!pub.exposure_profile : null,
        publicProfile: pub?.exposure_profile || null,
    };
}

export function usePeople() {
    const [state, setState] = useState(null);
    const load = useCallback(() => loadTies().then(setState), []);
    useEffect(() => {
        let live = true;
        loadTies().then((s) => { if (live) setState(s); });
        return () => { live = false; };
    }, []);
    return useMemo(() => ({
        loaded: state !== null,
        ...(state || { people: new Map(), friends: [], incoming: [], followAsks: [], followSent: [], sent: [], past: [], following: [], followers: [], publicOn: null }),
        entry: (id) => (id && state ? state.people.get(String(id)) || null : null),
        reload: load,
    }), [state, load]);
}

// PEOPLE YOU MAY KNOW: everyone you share a group with and have no live tie to
// yet. One members call per group, so only the Find page asks for it.
export async function loadMayKnow({ meId, tied }) {
    const groups = (await api.getGroups().catch(() => ({ groups: [] })))?.groups || [];
    const lists = await Promise.all(groups.map((g) => api.getGroupMembers(g.id).then((r) => r?.members || []).catch(() => [])));
    const out = new Map();
    lists.forEach((members, i) => {
        for (const m of members) {
            const id = String(m.user_id || m.user?.id || m.id);
            if (id === String(meId) || tied.has(id)) continue;
            const username = m.user?.username || m.username || null;
            // their name when the roster carries it, else their username
            const name = nameOf({ first_name: m.first_name ?? m.user?.first_name, last_name: m.last_name ?? m.user?.last_name, username });
            if (!out.has(id)) out.set(id, { id, name, username, groups: [] });
            out.get(id).groups.push({ id: String(groups[i].id), name: groups[i].name || 'a group' });
        }
    });
    return [...out.values()].sort(byName);
}

// Whether you are tied to someone in a way that hides them from "may know".
export const isTied = (e) => !!(e && (e.friend || e.incoming || e.sent || e.following || e.followSent));

// THE CONSENT PAGE, for every act on a person that shares something: asking to
// be friends, accepting, editing what a friend sees, the public profile. It
// comes back to the page you left, with the person still open, and says what
// happened (`done`); the return reads `code=success` (or the old success=true).
export function grantHref(query, back, done) {
    const u = new URL(back, window.location.origin);
    if (done) u.searchParams.set('done', done);
    return `/grant?${query}&redirect_uri=${encodeURIComponent(u.pathname + u.search)}`;
}

const DONE = {
    request: 'Friend request sent!',
    accepted: 'You are friends now.',
    shared: 'Saved what you share.',
    public: 'Your public profile is saved.',
};
const RETURN_PARAMS = ['done', 'code', 'success', 'client_id', 'user_id', 'username', 'sid_code', 'denied', 'error'];

// Back from the consent page: one toast, and the address cleaned of what the
// page handed back (the open item stays open).
export function useGrantReturn(onBack) {
    const [params, setParams] = useSearchParams();
    const toast = useToast();
    useEffect(() => {
        const ok = params.get('code') === 'success' || params.get('success') === 'true';
        const touched = RETURN_PARAMS.some((k) => params.has(k));
        if (!touched) return;
        if (ok) toast.success(DONE[params.get('done')] || DONE.request);
        setParams((p) => {
            const n = new URLSearchParams(p);
            RETURN_PARAMS.forEach((k) => n.delete(k));
            return n;
        }, { replace: true });
        if (ok) onBack?.();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
